import { motion } from "framer-motion";
import { Disc3, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";

// Animation variants
const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: "easeOut" }
};

export default function TermsPage() {
  return (
    <div className="bg-[#09090b] min-h-screen text-white">
      {/* Noise overlay */}
      <div className="fixed inset-0 z-[9999] pointer-events-none opacity-[0.03] mix-blend-overlay bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />
      
      {/* Header */}
      <Header />

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 pt-28 pb-16">
        <motion.div {...fadeUp}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-4">Legal</p>
          <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-4">
            Terms & Conditions
          </h1>
          <p className="text-zinc-500 text-sm mb-12">Last updated: 2026</p>
        </motion.div>

        <motion.div 
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.1 }}
          className="space-y-12 text-zinc-400 leading-relaxed"
        >
          {/* Section 1 */}
          <section>
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-4">
              1. Overview
            </h2>
            <p className="mb-4">
              Test Press Club ("TPC") is a private music circulation and pitching platform that reviews submitted tracks and, where appropriate, shares selected recordings with industry contacts including record labels, DJs and other music industry professionals.
            </p>
            <p className="mb-4">
              Submission of a track does not guarantee pitching, placement, or a deal.
            </p>
            <p>
              TPC operates as a curation and pitching service, selecting only recordings that fit current opportunities and industry demand.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-4">
              2. Rights Confirmation
            </h2>
            <p className="mb-4">
              By submitting music to Test Press Club you confirm that:
            </p>
            <ul className="list-none space-y-2 ml-0">
              <li className="flex items-start gap-3">
                <span className="text-neon mt-1">•</span>
                <span>You control or represent the rights to the recording submitted</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-neon mt-1">•</span>
                <span>You have permission from all contributors involved in the recording</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-neon mt-1">•</span>
                <span>The track does not infringe on any copyright or third-party rights</span>
              </li>
            </ul>
            <p className="mt-4">
              TPC accepts no responsibility for disputes relating to ownership.
            </p>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-4">
              3. Commission
            </h2>
            <p className="mb-4">
              If Test Press Club successfully facilitates a deal, placement, or commercial opportunity directly resulting from pitching your submitted track, the following commission applies:
            </p>
            <div className="border border-zinc-800 p-6 mb-4">
              <div className="flex justify-between items-center mb-3 pb-3 border-b border-zinc-800">
                <span className="text-zinc-500">Free Tier submissions</span>
                <span className="text-white font-bold">15% commission</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-zinc-500">Pro Access submissions</span>
                <span className="text-neon font-bold">10% commission</span>
              </div>
            </div>
            <p className="mb-4">Commission applies to:</p>
            <ul className="list-none space-y-2 ml-0">
              <li className="flex items-start gap-3">
                <span className="text-neon mt-1">•</span>
                <span>Label advances</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-neon mt-1">•</span>
                <span>Licensing fees</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-neon mt-1">•</span>
                <span>Buyouts</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-neon mt-1">•</span>
                <span>Sync fees</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-neon mt-1">•</span>
                <span>Other commercial agreements directly resulting from TPC pitching activity</span>
              </li>
            </ul>
            <p className="mt-4">
              Artists agree to notify Test Press Club if a deal occurs with a party introduced through TPC.
            </p>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-4">
              4. No Guarantee of Placement
            </h2>
            <p className="mb-4">TPC reviews all submissions but does not guarantee:</p>
            <ul className="list-none space-y-2 ml-0">
              <li className="flex items-start gap-3">
                <span className="text-neon mt-1">•</span>
                <span>Pitching of every submission</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-neon mt-1">•</span>
                <span>Placement with a label</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-neon mt-1">•</span>
                <span>DJ support</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-neon mt-1">•</span>
                <span>Commercial deals</span>
              </li>
            </ul>
            <p className="mt-4">
              Tracks are circulated only if they meet current opportunities and quality standards.
            </p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-4">
              5. Track Usage
            </h2>
            <p className="mb-4">
              By submitting music to Test Press Club you grant TPC permission to:
            </p>
            <ul className="list-none space-y-2 ml-0">
              <li className="flex items-start gap-3">
                <span className="text-neon mt-1">•</span>
                <span>Privately share the track with relevant industry contacts</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-neon mt-1">•</span>
                <span>Include the track in pitching emails or private listening pools</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-neon mt-1">•</span>
                <span>Evaluate the recording internally</span>
              </li>
            </ul>
            <p className="mt-4">
              TPC does not claim ownership of submitted recordings.
            </p>
            <p className="mt-2">
              All rights remain with the artist or rights holder.
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-4">
              6. Pro Access Membership
            </h2>
            <p className="mb-4">
              Pro Access is an optional paid membership providing:
            </p>
            <ul className="list-none space-y-2 ml-0">
              <li className="flex items-start gap-3">
                <span className="text-neon mt-1">•</span>
                <span>Priority review</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-neon mt-1">•</span>
                <span>Multiple submissions per month</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-neon mt-1">•</span>
                <span>Reduced commission rates</span>
              </li>
            </ul>
            <p className="mt-4">
              Membership does not guarantee pitching or placement.
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-4">
              7. Removal Requests
            </h2>
            <p>
              Artists may request the removal of their submitted track from TPC circulation at any time by contacting the platform.
            </p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-4">
              8. Contact
            </h2>
            <p className="mb-4">
              For inquiries regarding submissions or terms:
            </p>
            <div className="border border-zinc-800 p-6">
              <p className="text-white font-bold mb-2">Test Press Club</p>
              <p className="text-neon">www.testpressclub.com</p>
            </div>
          </section>
        </motion.div>

        {/* Back to home */}
        <motion.div 
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.2 }}
          className="mt-16 pt-8 border-t border-zinc-800"
        >
          <Link 
            to="/"
            className="text-zinc-500 hover:text-neon transition-colors duration-300 text-sm"
          >
            ← Back to Test Press Club
          </Link>
        </motion.div>
      </main>
    </div>
  );
}
